import { readJSON, writeJSON } from "../storage";
import type { MaraDailyData, MaraFetchResult } from "./types";

const MARA_PROXY_URL = "/api/mara";
const TTL_CURRENT_DAY_MS = 1000 * 60 * 60 * 12;
const TTL_PAST_DAY_MS = 1000 * 60 * 60 * 24 * 30;

const memCache = new Map<string, MaraDailyData>();

function cacheKey(cityId: number, isoDate: string): string {
  return `athkari.mara.v2.day.${cityId}.${isoDate}`;
}

function isPastMonth(year: number, month: number): boolean {
  const now = new Date();
  const nowYear = now.getFullYear();
  const nowMonth = now.getMonth() + 1;
  return year < nowYear || (year === nowYear && month < nowMonth);
}

function isCurrentDay(isoDate: string): boolean {
  return isoDate === omanTodayISO();
}

function readLocalCache(key: string, ttlMs: number): MaraDailyData | null {
  const entry = readJSON<{ value: MaraDailyData; savedAt: number } | null>(key, null);
  if (!entry || typeof entry.savedAt !== "number") return null;
  if (Date.now() - entry.savedAt > ttlMs) return null;
  return entry.value;
}

function writeLocalCache(key: string, data: MaraDailyData): void {
  writeJSON(key, { value: data, savedAt: Date.now() });
}

export async function fetchMaraDay(
  cityId: number,
  citySlug: string,
  isoDate: string,
  signal?: AbortSignal
): Promise<MaraFetchResult> {
  const year = Number(isoDate.slice(0, 4));
  const month = Number(isoDate.slice(5, 7));
  const day = Number(isoDate.slice(8, 10));
  const key = cacheKey(cityId, isoDate);
  const ttl = isCurrentDay(isoDate) ? TTL_CURRENT_DAY_MS : (isPastMonth(year, month) ? TTL_PAST_DAY_MS : TTL_CURRENT_DAY_MS);

  const mem = memCache.get(key);
  if (mem) return { data: mem, status: "ok" };

  const local = readLocalCache(key, ttl);
  if (local) {
    memCache.set(key, local);
    return { data: { ...local, source: "mara-cache" }, status: "ok" };
  }

  try {
    const response = await fetch(
      `${MARA_PROXY_URL}?${new URLSearchParams({
        cityId: String(cityId),
        citySlug,
        year: String(year),
        month: String(month),
        day: String(day)
      }).toString()}`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
        signal
      }
    );

    if (!response.ok) {
      throw new Error(`MARA proxy HTTP ${response.status}`);
    }

    const parsed = await response.json() as MaraDailyData;
    if (!parsed?.timings?.date || !parsed.officialDate?.hijriAr) {
      throw new Error("MARA: invalid proxy response");
    }

    writeLocalCache(key, parsed);
    memCache.set(key, parsed);
    return { data: parsed, status: "ok" };
  } catch (err) {
    if (signal?.aborted) return { data: null, status: "error", error: "aborted" };

    const stale = readJSON<{ value: MaraDailyData } | null>(key, null);
    if (stale?.value) {
      return { data: { ...stale.value, source: "mara-cache" }, status: "offline" };
    }

    return {
      data: null,
      status: "error",
      error: err instanceof Error ? err.message : String(err)
    };
  }
}

export function omanTodayISO(date = new Date()): string {
  const formatter = new Intl.DateTimeFormat("en", {
    timeZone: "Asia/Muscat",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  const parts = formatter.formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value ?? "0000";
  const month = parts.find((part) => part.type === "month")?.value ?? "00";
  const day = parts.find((part) => part.type === "day")?.value ?? "00";
  return `${year}-${month}-${day}`;
}

export function omanYearMonth(date = new Date()): { year: number; month: number } {
  const iso = omanTodayISO(date);
  return { year: parseInt(iso.slice(0, 4), 10), month: parseInt(iso.slice(5, 7), 10) };
}

export function omanDateParts(date = new Date()): { year: number; month: number; day: number; isoDate: string } {
  const isoDate = omanTodayISO(date);
  return {
    year: parseInt(isoDate.slice(0, 4), 10),
    month: parseInt(isoDate.slice(5, 7), 10),
    day: parseInt(isoDate.slice(8, 10), 10),
    isoDate
  };
}
